/**
 * 1NOW-11127: the class names a vehicle carries.
 *
 * FMS saves a vehicle's classes to `fleet_classes` (many-to-many, each entry
 * `{ label, value }`). `fleet_class` is the older single field and is empty for
 * anything classed in FMS, which left the Car type filter, the card badge and
 * the home category buttons with nothing to show.
 *
 * `fleet_classes` wins when it names anything; `fleet_class` is only the
 * fallback, so a stale single value cannot resurface after an operator clears
 * the classes in FMS.
 */
type ClassCar =
  | { fleet_class?: { name?: string | null } | null; fleet_classes?: unknown }
  | null
  | undefined;

const clean = (v: unknown) => (typeof v === "string" ? v.trim() : "");

export const fleetClassNames = (car: ClassCar): string[] => {
  const many: unknown[] = Array.isArray(car?.fleet_classes) ? (car!.fleet_classes as unknown[]) : [];
  const names = many
    .map((c) => (typeof c === "string" ? clean(c) : clean((c as any)?.label ?? (c as any)?.name)))
    .filter(Boolean);
  const single = clean(car?.fleet_class?.name);
  return Array.from(new Set(names.length ? names : single ? [single] : []));
};

/** Every class name across the fleet, once each, in first-seen order. */
export const uniqueFleetClassNames = (cars?: ClassCar[] | null): string[] =>
  Array.from(new Set((cars ?? []).flatMap(fleetClassNames)));

/** The vehicle has at least one of the selected classes. */
export const inAnyClass = (car: ClassCar, selected: string[]): boolean =>
  fleetClassNames(car).some((name) => selected.includes(name));

/** The vehicle has some class at all. */
export const hasAnyClass = (car: ClassCar): boolean => fleetClassNames(car).length > 0;
